<script>
import { formatDate } from '@lib/utils/format-date'
import { mapActions, mapState, mapGetters } from 'vuex'
import ApiKeyMixin from '@lib/mixins/api-key'
import scopesTable from './api-key-scopes.table'
import getExportedFilename from '@lib/utils/get-exported-filename'
import DeleteApiKey from './modals/delete-api-key'
import copyToClipboard from '@lib/utils/copy-to-clipboard'

export default {
  name: 'ApiKeyDetails',
  components: {
    DeleteApiKey,
  },
  mixins: [ApiKeyMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      table: null,
      modals: {
        delete: false,
      },
      revealCode: false,
      apiKeyScopes: 'permissions',
    }
  },
  computed: {
    ...mapState('apiKeys', ['apiKey']),
    ...mapGetters('me', [
      'canUpdateApiKey',
      'canDeleteApiKey',
      'canReadProvider',
    ]),
    ...mapGetters('providers', ['providers']),
    title() {
      return `API Key ${this.apiKey.name}`
    },
    status() {
      return this.apiKey && this.apiKey.active ? 'active' : 'inactive'
    },
    back() {
      return {
        name: 'api-keys',
      }
    },
    provider() {
      const { providerId } = this.apiKey

      return this.providers.find(provider => provider.id === providerId) || {}
    },
  },
  watch: {
    preparedScopes(v) {
      if (!this.table) this.table = scopesTable(this)
    },
  },
  async created() {
    const { id } = this
    const [error] = await this.showApiKey({ id })

    if (error) {
      this.$router.replace(this.back)
    }

    this.table = scopesTable(this)

    this.loading = false
  },
  methods: {
    ...mapActions('apiKeys', ['showApiKey']),
    formatDate,
    editApiKey() {
      this.$router.push({
        name: 'api-key-edit',
        params: {
          id: this.id,
        },
      })
    },
    getExportedFilename() {
      return getExportedFilename(this.table.tableName)
    },
    copyContent(content) {
      copyToClipboard(content, null, 2)
    },
  },
}
</script>

<template>
  <main-layout
    :title="title"
    :loading="loading"
    :back="back"
  >
    <el-dropdown
      v-if="canUpdateApiKey || canDeleteApiKey"
      slot="header"
      trigger="click"
    >
      <dropdown-icon />
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item
          v-if="canUpdateApiKey"
          @click.native="editApiKey"
        >
          Edit API Key
        </el-dropdown-item>
        <el-dropdown-item
          v-if="canDeleteApiKey"
          @click.native="modals.delete = true"
        >
          Delete API Key
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>

    <data-box
      v-if="apiKey"
      header="Details"
      :status="status"
    >
      <data-list>
        <dt>Date Created</dt>
        <dd>{{ formatDate(apiKey.createdAt) }}</dd>

        <dt>API Key ID</dt>
        <dd>{{ apiKey.id }}</dd>

        <dt>API Key Name</dt>
        <dd>{{ apiKey.name }}</dd>

        <dt>API Key</dt>
        <dd>
          <div :class="$style.secret">
            <div>
              <el-button
                icon="el-icon-lock"
                :class="$style.secretButton"
                @click.native="revealCode = !revealCode"
              />
            </div>
            <div :class="$style.secretText">
              <span v-if="revealCode">{{ apiKey.secret }}</span>
              <span
                v-else
                :class="$style.secretBulls"
              >
                &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;
              </span>
            </div>
            <div>
              <el-button
                icon="el-icon-copy-document"
                :class="$style.secretButton"
                @click="copyContent(apiKey.secret)"
              />
            </div>
          </div>
        </dd>

        <dt>Provider Name</dt>
        <dd>
          <router-link
            v-if="canReadProvider"
            :to="{name: 'provider-update', params: { id: provider.id }}"
          >
            {{ provider.name }}
          </router-link>
          <template v-else>
            {{ provider.name }}
          </template>
        </dd>

        <dt>Last Updated</dt>
        <dd>{{ formatDate(apiKey.updatedAt) }}</dd>
      </data-list>

      <hr class="divider-primary info-block__divider">

      <div :class="$style.title">
        Scopes
      </div>

      <table-layout
        v-if="table"
        table-name="api-key-scopes"
        :processor="table.processor"
        :filters="table.filters"
        :columns="table.columns"
        :table-controls="false"
        :flat="true"
        shadow="never"
        :export-filename="getExportedFilename"
        :class="$style.scopesTable"
      />
    </data-box>

    <delete-api-key
      v-if="modals.delete"
      :api-key="apiKey"
      :visible.sync="modals.delete"
    />
  </main-layout>
</template>

<style lang="scss" module>
.title {
  padding: 0 0 1.5rem;
  font-size: 1.4rem;
  font-weight: 600;
}

.secret {
  display: flex;
  align-items: center
}

.secretButton {
  padding: rem(4px);
}

.secretText {
  width: rem(150px);
  padding: 0 rem(6px);
}

.secretBulls {
  letter-spacing: 3.2px;
}

.scopesTable {
  max-width: rem(600px);
  border-width: 0;

  :global {
    .el-card__body {
      padding-top: 0;
    }
  }
}
</style>
